import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { addNewCountry, editCountry, editCountryImage, getAllCountries } from "../../../redux/features/countries/countriesService";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark, faXmark } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/Modal";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import SearchBar from "../../../components/SearchBar";
import TableSkeleton from "../../../components/Skeleton";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB Limit

const Country = () => {

    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const fileEditInputRef = useRef(null);

    const { isCountriesLoading, pageCount, countries } = useSelector((state) => state.country.allCountries);
    const { isCountryCreating } = useSelector((state) => state.country.newCountry);
    const { isEditingCountry } = useSelector((state) => state.country.editedCountry);

    const [formData, setFormData] = useState({
        name: '',
        file: '',
    });

    const [errorMessages, setErrorMessages] = useState({
        name: '',
        file: '',
    });
    const [disabledBtn, setBtnDisable] = useState(false);
    const [skip, setSkip] = useState(0);

    const limit = 10;  // Number of items per page
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [initialCountry, setInitialCountry] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
        file: ''
    });

    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchAllCountrys = useCallback(() => {
        dispatch(getAllCountries({
            skip: skip,
            limit,
            search: searchQuery
        }));
    }, [dispatch, skip, searchQuery]);

    useEffect(() => {
        fetchAllCountrys();
    }, [fetchAllCountrys]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const validateFile = (file) => {
        if (file) {
            if (!file.type.startsWith("image/")) {
                setErrorMessages({ ...errorMessages, file: "Only image files are allowed." });
                return;
            }

            if (file.size > MAX_FILE_SIZE) {
                setErrorMessages({ ...errorMessages, file: "File size exceeds 2MB limit." });
                return;
            }

            setErrorMessages({ ...errorMessages, file: "" });
            setFormData({ ...formData, file });
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const selectedFile = event.dataTransfer.files[0];

        if (selectedFile) {
            validateFile(selectedFile);
        }
    };

    const handleFileChange = (event) => {
        if (event.target.files.length > 0) {
            const selectedFile = event.target.files[0];
            validateFile(selectedFile);
        }
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };
    // Validate input
    const isValidCountry = () => {
        const errors = {};
        const trimmedName = formData.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (!formData.file) {
            errors.file = "Image is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewCountry = async () => {
        if (isValidCountry(formData)) {
            setBtnDisable(true);
            try {
                const response = await dispatch(addNewCountry(formData)).unwrap();
                setBtnDisable(false);
                setFormData({
                    name: '',
                    file: ''
                });
                fetchAllCountrys();
                showToast('success', response.message);
            }
            catch (error) {
                setBtnDisable(false);
                showToast('error', error.message);
            };
        }
    };

    // Edit country
    const handleSelectedCountryChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedCountry((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const validateEditFile = (file) => {
        if (file) {
            if (!file.type.startsWith("image/")) {
                setEditErrorMsgs({ ...editErrorMsgs, file: "Only image files are allowed." });
                return;
            }

            if (file.size > MAX_FILE_SIZE) {
                setEditErrorMsgs({ ...editErrorMsgs, file: "File size exceeds 2MB limit." });
                return;
            }

            setEditErrorMsgs({ ...editErrorMsgs, file: "" });
            setSelectedCountry({ ...selectedCountry, file });
        }
    };

    const handleEditDrop = (event) => {
        setErrorMsg(null);
        event.preventDefault();
        event.stopPropagation();

        const selectedFile = event.dataTransfer.files[0];

        if (selectedFile) {
            validateEditFile(selectedFile);
        }
    };

    const handleEditFileChange = (event) => {
        if (event.target.files.length > 0) {
            const selectedFile = event.target.files[0];
            validateEditFile(selectedFile);
        }
    };

    const handleEditClick = () => {
        setErrorMsg(null);
        fileEditInputRef.current.click();
    };

    const isValidEditCountry = () => {
        const errors = {};
        const trimmedName = selectedCountry.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (!selectedCountry.file && !selectedCountry.image_url) {
            errors.file = "Image is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialCountry[key] && key !== 'file'
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const editCountryDetails = async () => {
        if (!isValidEditCountry(selectedCountry)) return;

        const data = getChangedValues(selectedCountry);
        const hasChanges = Object.keys(data).some(key => key !== 'id');

        if (!hasChanges && !selectedCountry.file) {
            return setErrorMsg('There are no changes to update.');
        }

        setEditBtnDisable(true);

        try {
            if (hasChanges) {
                const response = await dispatch(editCountry(data)).unwrap();
                showToast('success', response.message);
            }

            if (selectedCountry.file) {
                const imageResponse = await dispatch(editCountryImage({
                    id: selectedCountry.id,
                    file: selectedCountry.file
                })).unwrap();
                showToast('success', imageResponse.message);
            }

            setSelectedCountry(null);
            setInitialCountry(null);
            setModalOpen(false);
            fetchAllCountrys();
        } catch (error) {
            showToast('error', error.message);
        } finally {
            setEditBtnDisable(false);
        }
    };

    //Activate Country
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedCountryId, setSelectedCountryId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(editCountry({ id: selectedCountryId, status: true })).unwrap();
            setSelectedCountryId(null);
            setInitialCountry(null);
            showToast('success', response.message);
            fetchAllCountrys();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }
    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(editCountry({ id: selectedCountryId, status: false })).unwrap();
            setSelectedCountryId(null);
            setInitialCountry(null);
            showToast('success', response.message);
            fetchAllCountrys();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    // Pagination
    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Country</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='name'
                                        name='name'
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder='Enter Country Name'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.name && (
                                        <p className={`text-danger`}>{errorMessages.name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Image<span className={`required-star`}>*</span>
                                    </label>
                                    <div
                                        onDrop={handleDrop}
                                        onDragOver={(event) => event.preventDefault()}
                                        onClick={handleClick}
                                        className={`drop-input`}
                                    >
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            style={{ display: "none" }}
                                        />
                                        {(!formData.file) ? <p>Drag & Drop a file here or click to select</p>
                                            :
                                            <div className={`inserted-file`}>
                                                <div className={`file-card`}>
                                                    <img src={URL.createObjectURL(formData.file)} alt='File Type' className={`left-icon`} />
                                                </div>
                                            </div>
                                        }
                                    </div>
                                    {errorMessages.file && (
                                        <p className={`text-danger`}>{errorMessages.file}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={insertNewCountry}
                        >
                            {isCountryCreating ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Countries</h1>
                    <SearchBar onSearch={(query) => setSearchQuery(query)} />
                </div>
                <div className={`search-card-body`}>
                    {isCountriesLoading ?
                       <TableSkeleton columns={["Country", "Image", "Status", "Actions"]} rowCount={10} height={20}/>
                        : countries.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Country</th>
                                            <th>Image</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {countries?.map((country) => (
                                            <tr key={country.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {country.name}
                                                </td>
                                                <td>
                                                    <img
                                                        src={country.image_url}
                                                        width={80}
                                                        alt="Country"
                                                    />
                                                </td>
                                                <td>{country.status ? "Active" : "Inactive"}</td>
                                                <td className={`actionsBtn`}>
                                                    <button className={`btn-edit-label`} onClick={() => {
                                                        setSelectedCountry(country);
                                                        setInitialCountry(country);
                                                        setEditBtnDisable(false);
                                                        setErrorMsg(null);
                                                        setEditErrorMsgs({
                                                            name: '',
                                                            country_code: ''
                                                        })
                                                        setModalOpen(true);
                                                    }}>Edit</button>
                                                    <button
                                                        className={country.status ? `btn-inactive` : `btn-active`}
                                                        onClick={() => {
                                                            setSelectedCountryId(country.id);
                                                            country.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                        }
                                                        }>{country.status ? 'Deactivate' : 'Acitvate'}</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Edit Country</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='name'
                                                name='name'
                                                value={selectedCountry.name || ''}
                                                onChange={handleSelectedCountryChange}
                                                placeholder='Enter Country Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.name && (
                                                <p className={`text-danger`}>{editErrorMsgs.name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className={`form-input-row`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Image<span className={`required-star`}>*</span>
                                            </label>
                                            <div
                                                onDrop={handleEditDrop}
                                                onDragOver={(event) => event.preventDefault()}
                                                onClick={handleEditClick}
                                                className={`drop-edit-input`}
                                            >
                                                <input
                                                    type="file"
                                                    ref={fileEditInputRef}
                                                    onChange={handleEditFileChange}
                                                    style={{ display: "none" }}
                                                />
                                                {(!selectedCountry.image_url && !selectedCountry.file) ? <p>Drag & Drop a file here or click to select</p>
                                                    :
                                                    <div className={`inserted-file`}>
                                                        <div className={`file-card`}>
                                                            <img src={selectedCountry.file ? URL.createObjectURL(selectedCountry.file) : selectedCountry.image_url} alt='File Type' className={`left-edit-icon`} />
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                            {editErrorMsgs.file && (
                                                <p className={`text-danger`}>{editErrorMsgs.file}</p>
                                            )}
                                            {errorMsg && (
                                                <p className={`text-danger`}>{errorMsg}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editCountryDetails}
                                    >
                                        {isEditingCountry ? <BeatLoader color='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='Country Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the Country?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='Country Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the Country?`}
                    colorType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default Country;
